/**
 * BookingRfqSections — the on-booking "request competitive quotes" controls (#75 c9),
 * mounted on the real production booking review (FInalView), below its insurance +
 * duty cards. Purely presentational / controlled: the parent (FInalView) owns the
 * selection state and, on "Create Booking", fires createRfq for the chosen sections
 * associated to the booking (decision #77). This component renders:
 *   - Cargo insurance: either/or radio — use the instant premium OR get competing
 *     broker quotes via RFQ (#51, mutually exclusive). The instant-premium option is
 *     GATED until a policy has actually been created for every insured commodity
 *     (Neil: validate the policy is created before letting the user select it).
 *   - Customs: additive toggle — also send to a broker for an RFQ on top of the
 *     instant duty & tax estimate (#29/#51).
 *   - Warehouse: request warehouse services via RFQ, with the SAME multi-row service
 *     editor the /rfq/new creator uses (type / units / days / start / notes) so a
 *     warehouse RFQ raised from a booking carries full detail for vendors to quote.
 *
 * It does NOT touch FInalView's existing insurance/duty/broker logic — it sits
 * alongside them and only collects the RFQ intent.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { Card, Radio, Checkbox, Tooltip } from 'antd';
import WarehouseEditor from './WarehouseEditor';

const INSURANCE_MODE = { ESTIMATE: 'estimate', RFQ: 'rfq' };

// A policy is "created" (billable as the instant premium) when its live quote came
// back created AND a policy id was issued — the exact predicate FInalView.sumPremiums
// (bookedOnly) uses to decide an insurance charge lands on the booking, so the radio
// can't offer an instant premium the booking wouldn't actually carry.
function allPoliciesCreated(insuranceArr) {
  return (
    Array.isArray(insuranceArr) &&
    insuranceArr.length > 0 &&
    insuranceArr.every(
      (item) => item?.policy_id != null && item?.quote?.result_code === 'quote_created'
    )
  );
}

export default function BookingRfqSections({
  insuranceMode = INSURANCE_MODE.ESTIMATE,
  insuranceArr = [],
  customsRfq = false,
  warehouseRfq = false,
  warehouseServices,
  onInsuranceMode,
  onCustomsRfq,
  onWarehouseRfq,
  onWarehouseServices,
}) {
  const sections = [];
  if (insuranceMode === INSURANCE_MODE.RFQ) sections.push('INSURANCE');
  if (customsRfq) sections.push('CUSTOMS');
  if (warehouseRfq) sections.push('WAREHOUSE');

  const instantPremiumReady = allPoliciesCreated(insuranceArr);
  // Before a policy exists the instant-premium radio is disabled — and must not also
  // appear *selected*. The parent defaults insuranceMode to ESTIMATE, so reflect "no
  // choice yet" until it's ready (or the user has picked RFQ). FInalView.usesInstantInsurance
  // is the real billing guard; this just keeps the control honest.
  const effectiveInsuranceMode =
    instantPremiumReady || insuranceMode !== INSURANCE_MODE.ESTIMATE ? insuranceMode : undefined;
  const instantPremiumRadio = (
    <Radio value={INSURANCE_MODE.ESTIMATE} disabled={!instantPremiumReady}>
      ✅ Use the instant premium
    </Radio>
  );

  return (
    <Card
      title="Additional services — estimate or request quotes"
      style={{ margin: '16px' }}
      className="no-padding"
    >
      <p style={{ color: '#888', marginTop: -8 }}>
        Optionally request competitive quotes for these alongside
        your booking. The RFQ is created with your booking when you confirm.
      </p>

      <div style={{ padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
        <div style={{ fontWeight: 500, marginBottom: 6 }}>Cargo insurance</div>
        <Radio.Group
          value={effectiveInsuranceMode}
          onChange={(e) => onInsuranceMode?.(e.target.value)}
        >
          {instantPremiumReady ? (
            instantPremiumRadio
          ) : (
            <Tooltip title="Create a policy first — click “Create Policy” in the insurance card above to use the instant premium.">
              {/* antd v3: a disabled control swallows pointer events, so the Tooltip
                  needs a wrapper element that can receive hover to ever show. */}
              <span>{instantPremiumRadio}</span>
            </Tooltip>
          )}
          <Radio value={INSURANCE_MODE.RFQ}>📨 Get competing broker quotes (RFQ)</Radio>
        </Radio.Group>
        {!instantPremiumReady && (
          <div style={{ fontSize: 12, color: '#faad14', marginTop: 4 }}>
            Create a policy in the insurance card above to use the instant premium, or request broker quotes.
          </div>
        )}
      </div>

      <div style={{ padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
        <div style={{ fontWeight: 500, marginBottom: 6 }}>Customs brokerage</div>
        <Checkbox
          checked={customsRfq}
          onChange={(e) => onCustomsRfq?.(e.target.checked)}
        >
          📨 Also send to a broker for an RFQ (on top of the duty &amp; tax estimate)
        </Checkbox>
      </div>

      <div style={{ padding: '8px 0' }}>
        <div style={{ fontWeight: 500, marginBottom: 6 }}>Warehouse services</div>
        <Checkbox
          checked={warehouseRfq}
          onChange={(e) => onWarehouseRfq?.(e.target.checked)}
        >
          📨 Request warehouse services (RFQ)
        </Checkbox>
        {warehouseRfq && (
          <div style={{ marginTop: 10 }}>
            <WarehouseEditor
              value={warehouseServices}
              onChange={(v) => onWarehouseServices?.(v)}
            />
          </div>
        )}
      </div>

      {sections.length > 0 && (
        <div style={{ marginTop: 12, color: '#1890ff' }}>
          An RFQ will be created for: <strong>{sections.join(', ')}</strong> — associated to this booking.
        </div>
      )}
    </Card>
  );
}

BookingRfqSections.propTypes = {
  /** Current insurance choice: 'estimate' (instant premium) | 'rfq'. */
  insuranceMode: PropTypes.oneOf([INSURANCE_MODE.ESTIMATE, INSURANCE_MODE.RFQ]),
  /** The booking's insured commodities (gates the instant-premium option). */
  insuranceArr: PropTypes.array,
  /** Whether to also send customs to a broker RFQ. */
  customsRfq: PropTypes.bool,
  /** Whether to request warehouse services via RFQ. */
  warehouseRfq: PropTypes.bool,
  /** Controlled WarehouseEditor value: { services: [...] }. */
  warehouseServices: PropTypes.object,
  onInsuranceMode: PropTypes.func,
  onCustomsRfq: PropTypes.func,
  onWarehouseRfq: PropTypes.func,
  onWarehouseServices: PropTypes.func,
};
